import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, LogOut, LayoutDashboard, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Picks", to: "/picks" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Sell", to: "/sell" },
  { label: "VIP", to: "/vip" },
  { label: "Blog", to: "/blog" },
  { label: "We Love", to: "/we-love" },
  { label: "About Us", to: "/about" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-neutral-950/95 border-b border-neutral-800"
    >
      <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-display text-2xl font-black text-[#E8956B] tracking-tighter text-glow-pink">BTC</span>
          <span className="text-[11px] tracking-[0.25em] text-white uppercase">Beauty Trade Club</span>
        </Link>

        <nav className="hidden md:flex gap-8 text-base font-semibold text-neutral-200">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className={`transition-colors hover:text-white ${
                location.pathname === n.to ? "text-[#E8956B]" : ""
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="header-search-btn"
            onClick={() => navigate("/shop")}
            className="text-neutral-300 hover:text-[#E8956B] transition-colors"
          >
            <Search size={20} />
          </button>

          {user ? (
            <>
              {user.role === "admin" && (
                <button
                  data-testid="header-admin-btn"
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-1.5 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-4 py-1.5 text-sm transition-all"
                >
                  <ShieldCheck size={16} /> <span className="hidden sm:block">Admin</span>
                </button>
              )}
              <button
                data-testid="header-bag-btn"
                onClick={() => navigate("/dashboard")}
                className="text-neutral-300 hover:text-[#E8956B] transition-colors"
              >
                <ShoppingBag size={20} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className="flex items-center gap-2 border border-[#E8956B]/60 rounded-full pl-1 pr-3 py-1 hover:bg-[#E8956B]/10 transition-colors"
                  >
                    <span className="w-7 h-7 rounded-full bg-[#E8956B] flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase()}
                    </span>
                    <span className="hidden sm:block text-sm font-semibold">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-neutral-200 text-neutral-900 w-52 shadow-xl">
                  <div className="px-2 py-1.5 text-xs text-neutral-500 capitalize">{user.role} account</div>
                  <DropdownMenuSeparator className="bg-neutral-200" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-[#E8956B]/20">
                    <LayoutDashboard size={16} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-sell" onClick={() => navigate("/sell")} className="cursor-pointer focus:bg-[#E8956B]/20">
                    <ShoppingBag size={16} className="mr-2" /> Sell an item
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-[#E8956B]/20">
                      <ShieldCheck size={16} className="mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-neutral-200" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer focus:bg-[#E8956B]/20 text-red-400">
                    <LogOut size={16} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <button
              data-testid="sign-in-btn"
              onClick={() => setAuthOpen(true)}
              className="border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-5 py-1.5 text-sm transition-all"
            >
              Sign In
            </button>
          )}

          <button className="md:hidden text-neutral-300" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-neutral-800 bg-neutral-950 px-5 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-neutral-300 hover:text-[#E8956B]">
              {n.label}
            </Link>
          ))}
        </nav>
      )}

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}

export { Heart };
